import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {Subscription} from 'rxjs';
import {Contract, contractDateToInput} from '@shared/api/models/query/contract.model';

// Strict parsing (the `true` flag in validDate) is ignored without this plugin.
dayjs.extend(customParseFormat);

export const CONTRACT_TITLE_MAX_LENGTH = 255;
export const CONTRACT_CODE_MAX_LENGTH = 50;
export const CONTRACT_SERVICE_PROVIDER_MAX_LENGTH = 255;

/** Value emitted by the form's `save` output. Dates are YYYY-MM-DD; empty optional fields are null. */
export interface ContractFormValue {
    title: string,
    code: string,
    serviceProvider: string | null,
    signatureDate: string | null,
    kickoffDate: string | null,
}

/** Whitespace-only counts as empty (same rule as `trimmedRequired` in license-deactivate.modal.ts). */
const trimmedRequired: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    (control.value ?? '').trim() === '' ? {required: true} : null;

/** An empty value is valid (optional field); anything else must be a real calendar date. */
const validDate: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value ?? '';
    return value === '' || dayjs(value, 'YYYY-MM-DD', true).isValid() ? null : {date: true};
};

/**
 * UNIVAPLAT-3516: shared contract form, used by the New contract page and (Sub-task 5) the
 * detail General tab. Presentational: it never calls the API. Kickoff earlier than signature
 * is accepted on purpose (no cross-field rule). Form content is never logged.
 */
@Component({
    selector: 'cdss-contract-form',
    standalone: true,
    imports: [ReactiveFormsModule],
    templateUrl: './contract-form.component.html',
    styleUrls: ['./contract-form.component.scss']
})
export class ContractFormComponent implements OnChanges, OnDestroy {
    readonly titleMaxLength = CONTRACT_TITLE_MAX_LENGTH;
    readonly codeMaxLength = CONTRACT_CODE_MAX_LENGTH;
    readonly serviceProviderMaxLength = CONTRACT_SERVICE_PROVIDER_MAX_LENGTH;

    /** Contract to pre-fill the form with (edit); empty form when absent. */
    @Input() contract?: Contract | null;
    /** No Save button and no editable field. */
    @Input() readOnly = false;
    /** Code field not editable while the others are (edit by a non-general admin or on edit). */
    @Input() codeReadOnly = false;
    /** A request is in flight: Save is disabled and announced as busy. */
    @Input() saving = false;
    /** Backend text for a duplicate code, shown on the Code field until the code is edited. */
    @Input() codeError: string | null = null;
    @Input() idPrefix = 'contract';

    @Output() save = new EventEmitter<ContractFormValue>();

    readonly form = new FormGroup({
        title: new FormControl<string>('', {
            nonNullable: true,
            validators: [trimmedRequired, Validators.maxLength(CONTRACT_TITLE_MAX_LENGTH)]
        }),
        code: new FormControl<string>('', {
            nonNullable: true,
            validators: [trimmedRequired, Validators.maxLength(CONTRACT_CODE_MAX_LENGTH)]
        }),
        serviceProvider: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.maxLength(CONTRACT_SERVICE_PROVIDER_MAX_LENGTH)]
        }),
        signatureDate: new FormControl<string>('', {nonNullable: true, validators: [validDate]}),
        kickoffDate: new FormControl<string>('', {nonNullable: true, validators: [validDate]}),
    });

    serverCodeError: string | null = null;

    private readonly codeSub: Subscription = this.form.controls.code.valueChanges.subscribe(() => {
        this.serverCodeError = null;
    });

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['contract']) {
            this.patchFromContract();
        }
        if (changes['readOnly'] || changes['codeReadOnly'] || changes['contract']) {
            this.applyEnabledState();
        }
        if (changes['codeError']) {
            this.serverCodeError = this.codeError || null;
            if (this.serverCodeError) {
                this.form.controls.code.setErrors({server: true});
                this.form.controls.code.markAsTouched();
            }
        }
    }

    ngOnDestroy(): void {
        this.codeSub.unsubscribe();
    }

    get canSave(): boolean {
        return !this.saving && this.form.valid;
    }

    onSubmit(): void {
        if (this.readOnly || this.saving) {
            return;
        }
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const raw = this.form.getRawValue();
        const serviceProvider = raw.serviceProvider.trim();
        this.save.emit({
            title: raw.title.trim(),
            code: raw.code.trim(),
            serviceProvider: serviceProvider === '' ? null : serviceProvider,
            signatureDate: raw.signatureDate || null,
            kickoffDate: raw.kickoffDate || null,
        });
    }

    /** True when the control shows an error: touched and invalid. */
    showError(name: keyof ContractFormValue): boolean {
        const control = this.form.controls[name];
        return control.invalid && (control.touched || control.dirty);
    }

    /** Ids of the error element(s) for a control, or null when none shown (for aria-describedby). */
    describedBy(name: keyof ContractFormValue): string | null {
        return this.showError(name) ? `${this.idPrefix}-${name}-error` : null;
    }

    private patchFromContract(): void {
        const contract = this.contract;
        this.form.reset({
            title: contract?.title ?? '',
            code: contract?.code ?? '',
            serviceProvider: contract?.serviceProvider ?? '',
            signatureDate: contractDateToInput(contract?.signatureDate),
            kickoffDate: contractDateToInput(contract?.kickoffDate),
        });
        this.serverCodeError = null;
    }

    private applyEnabledState(): void {
        if (this.readOnly) {
            this.form.disable({emitEvent: false});
            return;
        }
        this.form.enable({emitEvent: false});
        if (this.codeReadOnly) {
            this.form.controls.code.disable({emitEvent: false});
        }
    }
}
